import { Box } from "@mui/material";
import BookmarkPlan from "components/common/Bookmark";
import { filterPage } from "components/common/Filters/FiltersHelper";
import KeyMetrics from "components/common/KeyMetrics/KeyMetrics";
import BasicTable from "components/common/Table";
import Typography from "components/common/Typography";
import { setProgressInfoSummary } from "components/features/Progress/Progress.slice";
import { useTranslate } from "hooks/useTranslate";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setSorting } from "redux/sorting.slice";
import { tokens } from "styles/Tokens";
import { ABOVE_PROP_USER_TYPE, DEFAULT_CORP_INNCODE, corpViewTypes, sortableProperties } from "utils/CommonConstants";
import {
  decrypt,
  formFilterRequestPayload,
  functionalAreaIdToName,
  functionalAreaIdsToNames,
  getKeyMetricsPlansData,
  getKeyMetricsPlansDataSC,
  getMoreValues,
  getSourceName,
  getTableColor,
  isBookmarked
} from "utils/CommonFunctions";
import { tableauURL } from "utils/URLConstants";
import { fetchCorpDashboardPlansAsync, fetchCorpDashboardPlansOverviewAsync, reset } from "../CorpUser.Slice";
import { getAllBreakupLists, getmyListType } from "../MyLists/MyLists";
import TableEdit from "../MyLists/TableEdit";
import "./CorpView.scss";
import { dashboardPlansHeaders, plansHeaders, plansHeadersSC } from "./TableHeadersConfig";

const PlansData = ({ view, selectedListType, selectedList }: any) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const translate = useTranslate();

  const plansList = useAppSelector((state) => state.CorpDashboard.myView.plans.list);
  const functionalAreaList: any = useAppSelector((state) => state.functionalAreas.list);
  const overview = useAppSelector((state) => state.CorpDashboard.myView.plans.overview);
  const listCategory = useAppSelector((state) => state.CorpDashboard.myListsSelected.listCategory);
  const selectedTab = useAppSelector((state) => state.CorpDashboard.selectedTabs?.[view]);
  const selectedTabStr = selectedTab === 1 ? "plansSort" : "propertiesSort";
  const lobbyUserType = decrypt("salt", localStorage.getItem("lobbyUserType"));
  const tableName = selectedTabStr + "_" + view;
  const sorting = useAppSelector((state) => state.sortingDetails.sorting);
  const recentSortingApplied = sorting?.[tableName];
  const searchedText = useAppSelector((state) => state.CorpDashboard.searchText?.[view]);

  const globalFiltersforPage = useAppSelector(
    (state) =>
      state.globalFilters[
        selectedListType === "default"
          ? filterPage.dashboardDisplay
          : selectedListType === "consultant"
          ? filterPage.consultantListDisplay
          : filterPage.listDisplay
      ]
  );
  const dynamicSelectFlag = globalFiltersforPage?.dynamicSelectAll;
  const filterRequestPayload =
    view === corpViewTypes.dashboard
      ? globalFiltersforPage?.isFilterApplied
        ? globalFiltersforPage?.modifiedFilter
        : globalFiltersforPage?.defaultFilter
      : (isEmpty(globalFiltersforPage?.modifiedFilter)
          ? globalFiltersforPage?.defaultFilter
          : globalFiltersforPage?.modifiedFilter) || [];
  const formattedFilterRequestPayload = formFilterRequestPayload(filterRequestPayload);

  const myListType = getmyListType(selectedListType, selectedList?.name);

  const fetchCorpDashboardPlan = (payload: any, callOverview: boolean = false) => {
    let payloadData = { ...payload, view: view };
    payloadData =
      view === corpViewTypes.myList
        ? { ...payloadData, myListType: myListType, myListId: selectedList?.id }
        : payloadData;
    if (selectedTab === 1) {
      dispatch(fetchCorpDashboardPlansAsync({ ...payloadData }));
      if (callOverview)
        dispatch(
          fetchCorpDashboardPlansOverviewAsync({
            ...payloadData
          })
        );
    }
  };

  const apiCall = () => {
    let payloadData = { ...formattedFilterRequestPayload, ...dynamicSelectFlag, searchText: searchedText };
    const sorting = { sort: { ...recentSortingApplied } };
    payloadData = { ...payloadData, ...sorting };
    fetchCorpDashboardPlan(payloadData, true);
  };

  useEffect(() => {
    if (String(lobbyUserType).toLowerCase() !== ABOVE_PROP_USER_TYPE) apiCall();
  }, [filterRequestPayload]);

  useEffect(() => {
    return () => {
      dispatch(reset());
    };
  }, []);

  const getInputDetail = (val: string) => {
    let payloadData: any =
      view === corpViewTypes.myList
        ? { ...formattedFilterRequestPayload, myListType: myListType, myListId: selectedList?.id }
        : { ...formattedFilterRequestPayload, ...dynamicSelectFlag };

    const sorting = { sort: { ...recentSortingApplied } };

    payloadData = { ...payloadData, ...sorting, searchText: val };
    fetchCorpDashboardPlan(payloadData);
  };

  const getRowData = (plansList: any) => {
    return plansList?.map((plan: any) => {
      return {
        data: getChildObject(plan, plansList)
      };
    });
  };

  const getChildObject = (plan: any, plansList: any) => {
    const type = "plan";
    let data: any = [
      {
        content: (
          <Box
            onClick={() => {
              {
                if (selectedListType === "consultant") {
                  navigate(`/progressInfo/${type}/${selectedList?.id}?cmsId=${plan?.planCmsId}`, {
                    state: {
                      isMyLists: true,
                      defaultTab: 1,
                      innCodes: plan?.implementedInncodes
                    }
                  });
                  dispatch(setProgressInfoSummary(plan));
                } else
                  navigate(`/plandetails/${plan?.planCmsId}`, {
                    state: {
                      innCode: DEFAULT_CORP_INNCODE,
                      isMyLists: true,
                      defaultTab: 1,
                      innCodes: plan?.implementedInncodes,
                      redirectFromDashboard: true
                    }
                  });
              }
            }}
            sx={{
              textDecoration: "none",
              cursor: "pointer",
              color: tokens.Text.General.PrimaryHeadline,
              fontWeight: "700"
            }}
          >
            {plan?.planName || plan?.myPlanName}
          </Box>
        ),
        showBold: true
      },
      {
        content: getMoreValues(functionalAreaIdsToNames(plan.functionalAreas)),
        tooltip: {
          title: "Functional Areas",
          list: plan?.functionalAreas?.map((FA: any) => {
            return { name: functionalAreaIdToName(FA, functionalAreaList) };
          })
        }
      },
      {
        content: getMoreValues(plan?.planSources?.map((source: any) => getSourceName(source))),

        tooltip: {
          list: plan?.planSources?.map((src: any) => {
            return { name: getSourceName(src) };
          })
        }
      },

      {
        content: listCategory === "consultant" && view === "myList" ? plan.plansCount : plan.implementedPropertiesCount
      }
    ];
    if (view === "myList") {
      data.push({
        content: geStatusValues(plan?.implementedPlansCountStatusBreakUp)
      });
      data.push({
        content: (
          <BookmarkPlan
            innCodeForPayload={DEFAULT_CORP_INNCODE}
            cmsId={plan?.planCmsId}
            isBookmarked={isBookmarked(plan?.planCmsId, DEFAULT_CORP_INNCODE)}
            onClickFunction={() => {
              if (selectedList?.name === "Bookmarked Plans") {
                apiCall();
              }
            }}
          />
        )
      });
    }

    return data;
  };

  const geStatusValues = (statusBreakUp: any) => {
    const status = getAllBreakupLists(statusBreakUp);

    return (
      <>
        {status?.map((item: any) => {
          return (
            <div style={{ color: getTableColor(item?.name), whiteSpace: "nowrap" }}>
              <Typography component={"span"} variant={"body2"} showBold>
                {item.value < 10 ? "0" : ""}
                {`${item.value ?? "00"}  `}
              </Typography>
              <Typography component={"span"}>
                {item?.name} {item?.isShowStar > 0 ? "*" : ""}
              </Typography>
            </div>
          );
        })}
      </>
    );
  };

  const handlePlansSorting = (headerName: string) => {
    let header = sortableProperties[headerName];
    let sortedOrder = recentSortingApplied.order === "asc" ? "desc" : "asc";
    let payloadData = {
      ...formattedFilterRequestPayload,
      searchText: searchedText
    };
    payloadData = {
      ...payloadData,
      sort: {
        property: header,
        order: header == recentSortingApplied.property ? sortedOrder : "asc"
      }
    };

    fetchCorpDashboardPlan(payloadData);
    dispatch(setSorting({ tableName: tableName, header: headerName }));
  };

  return (
    <div className="corp-view-tab-data-component">
      <KeyMetrics
        helperText={
          listCategory === "consultant" && view === "myList"
            ? "*Results displayed are for highlighted list"
            : "*Last 30 Days"
        }
        hyperLink={
          view === "dashboard"
            ? {
                link: tableauURL,
                content: (
                  <Typography variant={"label"} color={tokens.Text.General.Color}>
                    Go to Tableau for full insights.
                  </Typography>
                )
              }
            : {}
        }
        keyMetricsData={
          listCategory === "consultant" && view === "myList"
            ? getKeyMetricsPlansDataSC(overview)
            : getKeyMetricsPlansData(overview)
        }
      />
      <TableEdit
        view={view}
        selectedListType={selectedListType}
        getInputDetails={(val: string) => getInputDetail(val)}
      />
      <BasicTable
        noDataMessage={translate("common.noResults")}
        data={{
          headers:
            view === "dashboard"
              ? dashboardPlansHeaders
              : listCategory === "consultant"
              ? plansHeadersSC
              : plansHeaders,
          rows: getRowData(plansList)
        }}
        handleSorting={handlePlansSorting}
        tableName={tableName}
      />
    </div>
  );
};
export default React.memo(PlansData);
